import { Injectable, signal, inject, DOCUMENT } from '@angular/core';

/**
 * Service to manage theme variable highlighting when form fields are focused
 * in the theme playground. Coordinates highlighting UI elements with matching
 * data-theme-key attributes and smoothly scrolls them into view.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeHighlightService {
  private document = inject(DOCUMENT);

  /**
   * Currently highlighted theme variable name (e.g., '--primary', '--bg-card')
   * Set to empty string when no variable is highlighted
   */
  public highlightedVariable = signal<string>('');

  /**
   * Check if user prefers reduced motion
   */
  private prefersReducedMotion(): boolean {
    return this.document.defaultView?.matchMedia('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /**
   * Set the currently highlighted theme variable and scroll matching elements into view
   * @param varName - CSS variable name (e.g., '--primary')
   */
  setHighlightedVariable(varName: string): void {
    this.highlightedVariable.set(varName);

    // Find all elements with matching data-theme-key attribute
    const elements = this.document.querySelectorAll(`[data-theme-key="${varName}"]`);

    if (elements.length > 0) {
      // Scroll the first matching element into view
      this.scrollToElement(elements[0] as HTMLElement);
    }
  }

  /**
   * Clear the current highlight
   */
  clearHighlight(): void {
    this.highlightedVariable.set('');
  }

  /**
   * Smoothly scroll an element into view, respecting prefers-reduced-motion
   * @param element - HTML element to scroll into view
   */
  private scrollToElement(element: HTMLElement): void {
    // Check if element is already visible in viewport
    const rect = element.getBoundingClientRect();
    const isVisible = (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (this.document.defaultView?.innerHeight || 0) &&
      rect.right <= (this.document.defaultView?.innerWidth || 0)
    );

    // If already visible, no need to scroll
    if (isVisible) {
      return;
    }

    // Determine scroll behavior based on user preference
    const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';

    // Use requestAnimationFrame for smooth performance
    requestAnimationFrame(() => {
      element.scrollIntoView({
        behavior: behavior,
        block: 'center',
        inline: 'nearest'
      });
    });
  }

  /**
   * Get the current highlighted variable name
   */
  getHighlightedVariable(): string {
    return this.highlightedVariable();
  }
}
